import { WprBaseDIManage } from './di/WprBaseDIManage';
import { WprControlInfo } from './control/WprControlInfo';
import { IWprConfigOption } from './control/IWprConfigOption';
import { WprBaseViewInfo } from './WprBaseViewInfo';
import { WprActionInfo } from '../action/WprActionInfo';
import { WprReflectUtil } from '../util/WprReflectUtil';
import { WprBaseViewMode } from '../mode/WprBaseViewMode';
import { WprBaseViewMultiMode } from '../mode/WprBaseViewMultiMode';
import { WprMultiMode } from '../mode/WprMultiMode';
import { WprKeyValue } from '../model/WprKeyValue';
import { WprValidError } from './validation/WprValidError';
import { WprViewError } from '../component/controls/WprViewError';
import { WprBaseExtention } from '../extention/WprBaseExtention';
import { WprModelActionInfo } from '../action/WprModelActionInfo';
import { WprBaseViewListMode } from '../mode/WprBaseViewListModeInfo';
import { WprListInfo } from './control/list/WprListInfo';
import { WprListInfoMap } from './control/list/WprListInfoMap';
import { WprValidErrorInfo } from './validation/WprValidErrorInfo';
import { WprRowInfo } from '../component/props/WprRowInfo';
import { WprValueMap } from '../model/WprValueMap';

/**
 * ビュー情報基本コアクラス
 */
export abstract class WprBaseViewCore extends WprBaseDIManage {
	// private 変数  ------------------------------------------------------------
	private m_ControlMap: Map<string, WprControlInfo>				= new Map<string, WprControlInfo>();		// コントロール情報マップ
	private m_ActionMap: Map<string, WprActionInfo>					= new Map<string, WprActionInfo>();			// アクション情報マップ
	private m_ModelActionMap: Map<string, WprModelActionInfo>		= new Map<string, WprModelActionInfo>();	// モデルアクション情報マップ
	private m_GlobalModelActionMap: Map<string, WprModelActionInfo>	= new Map<string, WprModelActionInfo>();	// グローバルモデルアクション情報マップ
	private m_ModelMap:  Map<string, string>;																	// モデルマップ
	private m_ListInfoMap: WprListInfoMap							= new WprListInfoMap(this);					// リスト情報マップ
	private m_ViewModeMap: Map<string, WprBaseViewMode> 			= new Map<string, WprBaseViewMode>();		// ビューモード情報マップ
	private m_InputErrorMsgList: WprValidErrorInfo[]				= null;										// 入力エラーメッセージ
	private m_ViewErrorList: WprViewError[]							= new Array(); 								// ビューエラー情報リスト
	private m_ExtentionMap: Map<string, WprBaseExtention> 			= new Map<string, WprBaseExtention>();		// 拡張機能マップ
	// --------------------------------------------------------------------------

	// プロパティ  ---------------------------------------------------------------
	/** ビュー情報 */
	public abstract get viewInfo(): WprBaseViewInfo;		public abstract set viewInfo(viewInfo: WprBaseViewInfo);
	/** モデルマップ */
	protected get modelMap(): Map<string, string> 			{ return this.getModelMap();		}
	/** コントロール情報マップ */
	public get controlMap(): Map<string, WprControlInfo> 	{ return this.m_ControlMap;			}
	/** アクション情報マップ */
	public get actionMap(): Map<string, WprActionInfo> 		{ return this.m_ActionMap;			}
	/** ビューモード情報マップ */
	public get viewModeMap(): Map<string, WprBaseViewMode> 	{ return this.m_ViewModeMap;		}
	/** リスト情報マップ */
	public get listInfoMap(): WprListInfoMap 				{ return this.m_ListInfoMap;		}
	/** リストデータマップ */
	public get listDataMap(): Map<string, string> 			{ return this.m_ListInfoMap.getListDataMap();	}
	/** ビュー状態 */
	public get invalid(): boolean 							{ return this.m_InputErrorMsgList != null;		}
	// --------------------------------------------------------------------------

	// abstract メソッド  -------------------------------------------------------
	/**
	 * モデル更新処理
	 * @param name コントロール名称
	 */
	public abstract refreshModel(name?: string): void;

	/**
	 * View(FormControlの値)更新処理
	 * @param name コントロール名称
	 */
	public abstract refreshView(name?: string): void;

	/**
	 * コントロール値変更処理
	 * @param name コントロール名
	 * @param val 値
	 */
	public abstract changeValue(name: string, val: any): void;

	/**
	 * リストコントロール値変更処理
	 * @param name コントロール名
	 * @param row 行データ
	 * @param val 値
	 */
	public abstract changeListValue(name: string, row: any, val: any): void;

	/**
	 * ビューモード変更処理
	 * @param mode モード情報
	 * @param oldMode 古いモード
	 * @param newMode 新しいモード
	 */
	public abstract changeViewMode(mode: WprBaseViewMode, oldMode: string, newMode: string): void;

	/**
	 * 複数ビューモード変更処理
	 * @param oldMode 変更前モード
	 * @param newMode 変更後モード
	 */
	public abstract changeViewMultiMode(mode: WprBaseViewMultiMode, oldMode: WprMultiMode, newMode: WprMultiMode): void;

	/**
	 * ビューデータ設定処理
	 * @param name ビューデータ名
	 * @param value ビューデータ
	 */
	public abstract setViewData(name: string, value: any): void;

	/**
	 * ビューデータ取得処理
	 * @param name ビューデータ名
	 * @returns ビューデータ
	 */
	public abstract getViewData(name: string): any;

	/**
	 * ビューモード設定処理
	 * @param name: ビューモード名
	 * @param mode モード
	 * @returns falseの場合、モードは変化がない
	 */
	public abstract setViewMode(name: string, mode: string): boolean;

	/**
	 * ビューモード取得処理
	 * @param name ビューモード名
	 * @returns ビューモード
	 */
	public abstract getViewMode(name: string): any;

	/**
	 * View(FormControlの値)クリア処理
	 * @param name コントロール名称
	 */
	public abstract clearView(name?: string): void;

	/**
	 * スコープモデル設定処理
	 * @param name モデル名称
	 * @param model モデル
	 */
	public abstract setScopeModel(name: string, model: any): void;

	/**
	 * スコープモード設定処理
	 * @param name モード名称
	 * @param mode モード
	 * @returns falseの場合、モードは変化がない
	 */
	public abstract setScopeMode(name: string, mode: any): boolean;

	/**
	 * スコープモード取得処理
	 * @param name スコープモード名
	 * @returns スコープモード
	 */
	public abstract getScopeMode(name: string): any;

	/**
	 * グローバルスコープモデル設定処理
	 * @param name モデル名称
	 * @param model モデル
	 */
	public abstract setGlobalScopeModel(name: string, model: any): void;

	/**
	 * グローバルモード設定処理
	 * @param name モード名称
	 * @param mode モード
	 * @returns falseの場合、モードは変化がない
	 */
	public abstract setGlobalMode(name: string, mode: any): boolean;

	/**
	 * グローバルモード取得処理
	 * @param name モード名
	 * @returns モード
	 */
	public abstract getGlobalMode(name: string): any;

	/**
	 * モードチェック
	 * @param mode モード
	 * @returns trueの場合、モードは含まれている
	 */
	public abstract checkMode(mode: string): boolean;

	/**
	 * サービスエラー通知処理
	 * @param url URL
	 * @param status ステータス
	 * @param message エラーメッセージ 
	 * @param error エラー情報
	 */
	public abstract onServiceError(url: string, status: string, message: string, error?: Error): void;

	/**
	 * 次のPathに移動する
	 * @param path 移動するパス
	 * @param isValid バリデーション実行フラグ
	 */
	public abstract pushPath(path: string, isValid?: boolean): void;

	/**
	 * 前のPathに戻る
	 * @param isValid バリデーション実行フラグ
	 */
	public abstract goBack(isValid?: boolean): void;
	// --------------------------------------------------------------------------

	// virtual メソッド  ---------------------------------------------------------
	/**
	 * コントロール定義設定
	 */
	public onRegisterConfig(): void {}

	/**
	 * アクション登録
	 */
	public onRegisterAction(): void {}

	/**
	 * ビュー表示通知
	 */
	public onShowView(): void {}

	/**
	 * 値変更通知
	 * @param name 名前
	 * @param value 値
	 * @returns falseの場合、変更を取り消す
	 */
	public onChangeValue(name: string, value: any): boolean {
		return true;
	}

	/**
	 * リスト値変更通知
	 * @param name 名前
	 * @param row 行データ
	 * @param value 値
	 * @returns falseの場合、変更を取り消す
	 */
	public onChangeListValue(name: string, row: any, value: any): boolean {
		return true;
	}

	/**
	 * ビューモード変更通知
	 * @param mode モード情報
	 * @param oldMode 古いモード
	 * @param newMode 新しいモード
	 */
	public onChangeViewMode(mode: WprBaseViewMode, oldMode: string, newMode: string): void {}

	/**
	 * 複数ビューモード変更通知
	 * @param oldMode 変更前モード
	 * @param newMode 変更後モード
	 */
	public onChangeViewMultiMode(mode: WprBaseViewMultiMode, oldMode: WprMultiMode, newMode: WprMultiMode): void {}

	/**
	 * ValueMap再設定処理
	 * @param name コントロール名
	 * @param id ValueMapID
	 * @list ValueMapのKey-Valueリスト
	 * @returns 再設定したKey-Valueリスト
	 */
	public onResetValueMap(name: string, id: string, list: WprKeyValue[]): WprKeyValue[] {
		return list;
	}

	/**
	 * ValueMap再設定処理(リスト用)
	 * @param name コントロール名
	 * @param id ValueMapID
	 * @param row 行データ
	 * @param list ValueMapのKey-Valueリスト
	 * @returns 再設定したKey-Valueリスト
	 */
	public onResetListValueMap(name: string, id: string, row: WprRowInfo, list: WprKeyValue[]): WprKeyValue[] {
		return list;
	}

	/**
	 * ビュー表示モード処理
	 */
	public showMode(): void {
		this.m_ViewModeMap.forEach((minfo, key, map) => {
			try {
				minfo.onShowView();
			}
			catch (ex) {
				minfo.addErrorLog('onShowModeで例外が発生しました。', ex);
			}
		});
	}

	/**
	 * 入力チェック（独自のValidationを実施する場合に使用する）
	 *   エラー追加例：WprValidError.addError('エラーメッセージ');
	 * 				  this.setControlError('control', 'error');
	 * @param actionName アクション名
	 * @param validError バリデーションエラー情報
	 * @param row 行情報
	 */
	protected onValid(actionName: string, validError: WprValidError, row?: any): void {
	}

	/**
	 * バリデーションエラー通知
	 * @param actionName アクション名
	 * @param validError バリデーションエラー情報
	 * @param row 行情報
	 * @バリデーションエラー情報
	 */
	protected onValidError(actionName: string, validError: WprValidError, row?: any): void {
	}

	/**
	 * バリデーションエラーメッセージ通知
	 * @param name コントロール名
	 * @param rowIndex 行数(Listの場合)
	 * @param validatorName バリデーション名
	 * @param msg エラーメッセージ
	 * @returns エラーメッセージ（変更する場合に指定する、メッセージを使用しない場合は、nullを返す）
	 */
	public onInvalidMessage(name: string, rowIndex: number, validatorName: string, msg: string): string {
		return msg;
	}
	
	/**
	 * クリア時にvalueに設定する値を取得する
	 * @param name コントロール名称
	 * @param value 値
	 * @returns クリア時にvalueに設定する値
	 */
	public onGetClearValue(name: string, value: any): any {
		return '';
	}

	/**
	 * リストクリア時にvalueに設定する値を取得する
	 * @param name コントロール名称
	 * @param row 行データ
	 * @param value 値
	 * @returns クリア時にvalueに設定する値
	 */
	public onGetListClearValue(name: string, listName: string, row: any, value: any): any {
		return '';
	}

	/**
	 * ロストフォーカス通知
	 * @param name コントロール名称
	 * @param value コントロール値
	 * @returns コントロール値（コントロール値を変更しない場合は、valueをそのまま返す）
	 */
	public onBlur(name: string, value: any): any {
		return value;
	}

	/**
	 * アクションチェック処理
	 * @param name アクション名
	 * @returns falseの場合はアクションは登録されていない
	 */
	public checkAction(action: string): boolean {
		return this.m_ActionMap.has(action);
	}

	/**
	 * ビュー表示終了処理
	 */
	public onEndShowView() {
	}
	// --------------------------------------------------------------------------

	// public メソッド  ---------------------------------------------------------
	/**
	 * コントロール定義登録処理
	 */
	public registerConfig(): void {
		try {
			this.onRegisterConfig();
		}
		catch (ex) {
			this.addErrorLog('onRegisterConfigで例外が発生しました。', ex);
		}
	}

	/**
	 * アクション登録処理
	 */
	public registerAction(): void {
		try {
			this.onRegisterAction();
		}
		catch (ex) {
			this.addErrorLog('onRegisterActionで例外が発生しました。', ex);
		}
	}

	/**
	 * コントロール定義追加
	 * @param name 名前
	 * @param description 説明
	 * @param modelName モデル名
	 * @param option オプション
	 */
	public addConfig(name: string, description: string, modelName: string, option?: IWprConfigOption): void {
		if (this.m_ControlMap.has(name) === false) {
			const cinfo = new WprControlInfo(this, name, description, modelName, option);
			if (cinfo.modelName != null && this.getModelPropertyName(cinfo.modelName) == null && this.viewInfo.getModelPropertyName(cinfo.modelName) == null)
				this.addErrorLog(`コントロール定義のモデル名の指定に誤りがあります。[${name}][${cinfo.modelName}]`);
			this.m_ControlMap.set(name, cinfo);
		}
		else {
			this.addErrorLog(`同一のコントロール定義が既に登録されています。[${name}]`);
		}
	}

	/**
	 * アクション登録処理
	 * @param name アクション名
	 * @param actionMethod アクションメソッド
	 * @param isValid バリデーション実行フラグ
	 * @param isEvent イベント情報使用フラグ
	 */
	protected addAction(name: string, actionMethod: (param: any) => void, isValid: boolean = false, isEvent: boolean = false) {
		if (this.m_ActionMap.has(name) === false) {
			const ainfo = new WprActionInfo(name, this, actionMethod, isValid, isEvent);
			this.m_ActionMap.set(name, ainfo);
			if (this.m_ControlMap.has(name) === false)
				this.addConfig(name, name, null);
		}
		else {
			this.addErrorLog(`同一のアクションが既に登録されています。[${name}]`);
		}
	}

	/**
	 * ロストフォーカスアクション登録処理
	 * @param name アクション名
	 * @param actionMethod リストアクションメソッド
	 * @param isValid バリデーション実行フラグ
	 * @param isEvent イベント情報使用フラグ
	 */
	protected addBlurAction(name: string, actionMethod: (param: any) => void, isValid: boolean = false, isEvent: boolean = false): void {
		const aname = `${name}@@blur`;
		if (this.m_ActionMap.has(aname) === false) {
			const ainfo = new WprActionInfo(name, this, actionMethod, isValid, isEvent);
			ainfo.isBlur = true;
			this.m_ActionMap.set(aname, ainfo);
		}
		else {
			this.addErrorLog(`同一のアクションが既に登録されています。[${name}]`);
		}
	}

	/**
	 * スコープモデル更新アクション登録処理
	 * @param modelName スコープモデル名
	 * @param actionMethod アクションメソッド
	 * @param propName プロパティ名
	 */
	protected addModelAction(modelName: string, actionMethod: (modelName: string, model: any, propName: string) => void, propName?: string): void {
		let mname = modelName;
		if (propName)
			mname = `${modelName}@${propName}`;
		if (this.m_ModelActionMap.has(mname) === false) {
			// if (this.getModelPropertyName(modelName) == null && this.viewInfo.getModelPropertyName(modelName) == null)
			// this.addErrorLog(`addModelActionのモデル名の指定に誤りがあります。[${modelName}]`);
			const ainfo = new WprModelActionInfo(modelName, actionMethod, propName);
			this.m_ModelActionMap.set(mname, ainfo);
			if (propName)
				this.viewInfo.setScopeModelUpdateProperty(this, modelName, propName);
		}
		else {
			this.addErrorLog(`同一のモデルアクションが既に登録されています。[${mname}]`);
		}
	}

	/**
	 * スコープモデル更新アクション登録処理
	 * @param modelName スコープモデル名
	 * @param actionMethod アクションメソッド
	 * @param propName プロパティ名
	 */
	protected addGlobalModelAction(modelName: string, actionMethod: (modelName: string, model: any, propName: string) => void, propName?: string) {
		if (this.m_GlobalModelActionMap.has(modelName) === false) {
			const ainfo = new WprModelActionInfo(modelName, actionMethod, propName);
			if (propName) {
				this.m_GlobalModelActionMap.set(`${modelName}@${propName}`, ainfo);
				this.viewInfo.setGlobalScopeModelUpdateProperty(this, modelName, propName);
			}
			else {
				this.m_GlobalModelActionMap.set(modelName, ainfo);
			}
		}
	}

	/**
	 * モデルプロパティ名取得
	 * @param modelName
	 */
	public getModelPropertyName(modelName: string): string {
		if (this.modelMap) {
			if (this.modelMap.has(modelName) === true)
				return this.modelMap.get(modelName);
		}
		return null;
	}

	/**
	 * モデル情報追加処理
	 * @param modelName モデル名称
	 * @param propName プロパティ名
	 */
	public addModelInfo(modelName: string, propName: string): void {
		if (this.m_ModelMap === undefined)
			this.m_ModelMap = new Map<string, string>();
		if (this.m_ModelMap.has(modelName) === false)
			this.m_ModelMap.set(modelName, propName);
		else
			this.addErrorLog(`同一の@WprModelが既に登録されています。[${modelName}]`);
	}

	/**
	 * コントロール情報取得処理
	 * @param name コントロール名
	 * @param listName リスト名
	 * @param index 行インデクス
	 * @returns コントロール情報
	 */
	public getControlInfo(name: string, listName?: string, index?: number): WprControlInfo {
		if (this.m_ControlMap.has(name) === true)
			return this.m_ControlMap.get(name);
		return this.m_ListInfoMap.getListControlInfo(listName, name, index);
	}

	/**
	 * アクション情報取得
	 * @param controlName コントロール名
	 * @param listName リスト名
	 * @return アクション情報
	 */
	public getActionInfo(controlName: string, listName?: string): WprActionInfo {
		if (this.m_ActionMap.has(controlName) === true) {
			const action = this.m_ActionMap.get(controlName);
			if (listName && action.isList === false)
				return null;
			return action;
		}
		return null;
	}

	/**
	 * 入力チェック処理
	 * @param name アクション名又はコントロール名
	 * @param isView メインビューフラグ
	 * @param isControl コントロールチェックフラグ
	 * @param isEdit 編集フラグ
	 * @param row 行情報
	 * @returns falseの場合入力エラー
	 */
	public checkValidate(name: string, isView: boolean = false, isControl: boolean = true, isEdit: boolean = false, row?: any): boolean {
		try {
			this.m_InputErrorMsgList = null;
			const validError = new WprValidError(this);
			if (row) {
				this.listInfoMap.checkValidate(name, validError, isControl, row);
			}
			else if (isView === false) {
				this.controlMap.forEach((cinfo, key, map) => {
					cinfo.checkValidate(name, validError, isControl, isEdit);
				});
			}
			else {
				this.viewInfo.controlMap.forEach((cinfo, key, map) => {
					cinfo.checkValidate(name, validError, isControl, isEdit);
				});
				for (const subView of this.viewInfo.subViewMap.values()) {
					subView.controlMap.forEach((cinfo, key, map) => {
						cinfo.checkValidate(name, validError, isControl, isEdit);
					});
				}
			}
			try {
				this.onValid(name, validError, row);
			}
			catch (ex) {
				this.addErrorLog('onValidで例外が発生しました。', ex);
			}
			if (validError.isError === true) {
				try {
					this.onValidError(name, validError, row);
				}
				catch (ex) {
					this.addErrorLog('onValidErrorで例外が発生しました。', ex);
				}
				if (isControl === false)
					this.m_InputErrorMsgList = validError.errorInfoList;
			}
			return !validError.isError;
		}
		catch (ex) {
			this.addErrorLog('入力チェック処理で例外が発生しました。', ex);
			return false;
		}
		finally {
			this.refreshViewErrorList();
		}
	}

	/**
	 * コントロール入力チェック
	 * @param name コントロール名
	 * @returns falseの場合入力エラー
	 */
	public checkControlValidate(name: string): boolean {
		return this.checkValidate(name, true, true, true);
	}

	/**
	 * リスト項目入力チェック
	 * @param name コントロール名
	 * @param row 行情報
	 * @returns falseの場合入力エラー
	 */
	public checkListValidate(name: string, row?: any): boolean {
		return this.checkValidate(name, true, true, true, row);
	}

	/**
	 * ビューモード情報初期化
	 */
	public initViewModeInfo() {
		this.FW.model.getDecoNameList('ViewMode', this).forEach((mname) => {
			const viewMode = WprReflectUtil.getProperty(this, mname) as WprBaseViewMode;
			if (viewMode) {
				if (this.m_ViewModeMap.has(viewMode.name) === false) {
					viewMode.setViewCore(this);
					this.m_ViewModeMap.set(viewMode.name, viewMode);
				}
				else {
					this.addErrorLog(`同一の@WprViewModeが既に登録されています。[${viewMode.name}][${viewMode.objectName}]`);
				}
			}
		});
	}

	/**
	 * ビューモード変更処理
	 * @param mode モード情報
	 * @param oldMode 古いモード
	 * @param newMode 新しいモード
	 */
	public changeMode(mode: WprBaseViewMode, oldMode: string, newMode: string): void {
		try {
			this.onChangeViewMode(mode, oldMode, newMode);
		}
		catch (ex) {
			this.addErrorLog('onChangeViewModeで例外が発生しました。', ex);
		}
		this.m_ExtentionMap.forEach((einfo, key, map) => {
			try {
				einfo.onChangeViewMode(mode, oldMode, newMode);
			}
			catch (ex) {
				einfo.addErrorLog('onChangeViewModeで例外が発生しました。', ex);
			}
		});
	}

	/**
	 * 複数ビューモード変更処理
	 * @param oldMode 変更前モード
	 * @param newMode 変更後モード
	 */
	public changeMultiMode(mode: WprBaseViewMultiMode, oldMode: WprMultiMode, newMode: WprMultiMode): void {
		try {
			this.onChangeViewMultiMode(mode, oldMode, newMode);
		}
		catch (ex) {
			this.addErrorLog('onChangeViewMultiModeで例外が発生しました。', ex);
		}
		this.m_ExtentionMap.forEach((einfo, key, map) => {
			try {
				einfo.onChangeViewMultiMode(mode, oldMode, newMode);
			}
			catch (ex) {
				einfo.addErrorLog('onChangeViewMultiModeで例外が発生しました。', ex);
			}
		});
	}

	/**
	 * モードコントロール値変更処理
	 * @param name コントロール名
	 * @param val 値
	 */
	public changeModeValue(name: string, val: any): void {
		this.m_ViewModeMap.forEach((minfo, key, map) => {
			try {
				minfo.onChangeValue(name);
			}
			catch (ex) {
				minfo.addErrorLog('onChangeValueで例外が発生しました。', ex);
			}
		});
	}

	/**
	 * 拡張機能コントロール値変更処理
	 * @param name コントロール名
	 * @param val 値
	 */
	public changeExtentionValue(name: string, val: any): void {
		this.m_ExtentionMap.forEach((einfo, key, map) => {
			try {
				einfo.onChangeValue(name, val);
			}
			catch (ex) {
				einfo.addErrorLog('onChangeValueで例外が発生しました。', ex);
			}
		});
	}

	/**
	 * ビューモード更新処理
	 */
	public updateMode(isClear?: boolean): void {
		this.m_ViewModeMap.forEach((minfo, key, map) => {
			try {
				minfo.updateMode(isClear);
			}
			catch (ex) {
				minfo.addErrorLog('updateModeで例外が発生しました。', ex);
			}
		});
	}

	/**
	 * 入力エラー状態取得
	 * @return trueの場合、入力エラー
	 */
	public getInputError(): boolean {
		return (this.m_InputErrorMsgList != null);
	}

	/**
	 * 入力エラーメッセージ取得
	 * @return 入力エラーメッセージ
	 */
	/** 入力エラーメッセージ */
	public getInputErrorMsgList(): WprValidErrorInfo[] {
		return this.m_InputErrorMsgList;
	}

	/**
	 * ビューエラー情報登録処理
	 * @param viewError ビューエラー情報
	 * @param subView サブビュー名
	 */
	public addViewErrorControl(viewError: WprViewError, subView?: string): void {
		if (this.m_ViewErrorList.includes(viewError) === false)
			this.m_ViewErrorList.push(viewError);
	}

	/**
	 * 入力エラーメッセージ追加
	 * @param list 入力エラーメッセージ
	 */
	public setInputErrorList(list: WprValidErrorInfo[]) {
		this.m_InputErrorMsgList = list;
		this.refreshViewErrorList();
	}

	/**
	 * コントロールエラー設定
	 * @param name コントロール名
	 * @param err エラー名
	 * @param errFlg エラーフラグ
	 * @param listName リスト名
	 * @param index 行インデクス
	 */
	public setControlError(name: string, err: string, listName?: string, index?: number) {
		const cinfo = this.viewInfo.getControlInfo(name, listName, index);
		if (cinfo) {
			cinfo.setValidError(name, err);
			this.refreshViewErrorList();
		}
		else {
			this.addErrorLog(`setControlError：コントロール名に誤りがあります。[${name}]`);
		}
	}

	/**
	 * コントロールエラークリア
	 * @param name コントロール名
	 * @param err エラー名
	 * @param listName リスト名
	 * @param index 行インデクス
	 */
	public clearControlError(name: string, err: string = null, listName?: string, index?: number) {
		const cinfo = this.viewInfo.getControlInfo(name, listName, index);
		if (cinfo) {
			cinfo.clearValidError(name, err);
			this.refreshViewErrorList();
		}
		else {
			this.addErrorLog(`setControlError：コントロール名に誤りがあります。[${name}]`);
		}
	}

	/**
	 * ビュー拡張機能初期化
	 */
	public initExtentionInfo() {
		this.FW.model.getDecoNameList('Extention', this).forEach((ename) => {
			const extention = WprReflectUtil.getProperty(this, ename) as WprBaseExtention;
			if (extention) {
				if (this.m_ExtentionMap.has(extention.name) === false) {
					extention.setViewCore(this);
					this.m_ExtentionMap.set(extention.name, extention);
				}
				else {
					this.addErrorLog(`同一の@Extentionが既に登録されています。[${extention.name}][${extention.objectName}]`);
				}
			}
		});
	}

	/**
	 * モデル更新通知処理
	 * @param name モデル名
	 * @param model モデル
	 * @param isGlobal グローバルフラグ
	 * @param prop プロパティ名
	 */
	public updateModel(name: string, model: any, isGlobal: boolean, prop?: string): void {
		try {
			if (prop)
				name = `${name}@${prop}`;
			if (isGlobal === false) {
				if (this.m_ModelActionMap.has(name) === true)
					this.m_ModelActionMap.get(name).execute(this, model);
			}
			else {
				if (this.m_GlobalModelActionMap.has(name) === true)
					this.m_GlobalModelActionMap.get(name).execute(this, model);
			}
		}
		catch (ex) {
			this.addErrorLog(`モデル更新アクションの実行で例外が発生しました。[${name}]`, ex);
		}
	}

	/**
	 * ビューモード名チェック処理
	 * @param viewMode ビューモード名
	 * @returns tureの場合、ビューモードが含まれている
	 */
	public checkViewModeName(viewMode: string): boolean {
		for(const minfo of this.m_ViewModeMap.values()) {
			if (minfo.name === viewMode)
				return true;
		}
		return false;
	}

	/**
	 * ビューモードチェック処理
	 * @param viewMode ビューモード名
	 * @param mode モード
	 * @returns tureの場合、モードが含まれている
	 */
	public checkViewMode(viewMode: string, mode: string): boolean {
		for(const minfo of this.m_ViewModeMap.values()) {
			if (!viewMode || minfo.name === viewMode) {
				if (minfo.containsMode(mode) === true)
					return true;
			}
		}
		return false;
	}

	/**
	 * モードリストデータ設定
	 * @param listName リスト名
	 * @param listData リストデータ
	 */
	public setViewModeListData(listName: string, listData: Array<any>) {
		this.viewModeMap.forEach((mode, key, map) => {
			if (mode.listName === listName) {
				const tmode = mode as WprBaseViewListMode<any, any>;
				tmode.setListData(listData);
			}
		});
	}

	/**
	 * ValueMap登録
	 * @param id ValueMapのID
	 * @param vm ValueMap
	 */
	public addValueMap(id: string, vm: WprValueMap): void {
		this.FW.model.addValueMap(id, vm);
	}

	/**
	 * ValueMap登録チェック処理
	 * @param id ValueMapのID
	 * @returns trueの場合、ValueMapは登録済み
	 */
	public hasValueMap(id: string): boolean {
		return this.FW.model.hasValueMap(id);
	}

	/**
	 * Blurイベント停止処理
	 */
	public stopBlurEvent() {
		this.FW.view.stopBlurEvent();
	}

	/**
	 * Blurイベント再開処理
	 */
	public restartBlurEvent() {
		this.FW.view.restartBlurEvent();
	}
	// --------------------------------------------------------------------------

	// ListInfoMap メソッド  -----------------------------------------------------
	/**
	 * リストコントロール定義追加
	 * @param name 名前
	 * @param description 説明
	 * @param tableName リスト名
	 * @param option オプション
	 * @param checkError エラーチェックフラグ
	 * @returns リスト情報
	 */
	public addListConfig(name: string, description: string, listName: string, option?: IWprConfigOption, checkError: boolean = true): WprListInfo {
		return this.m_ListInfoMap.addListConfig(name, description, listName, option, checkError);
	}

	/**
	 * リストアクション登録処理
	 * @param name アクション名
	 * @param listName リスト名
	 * @param actionMethod リストアクションメソッド
	 * @param isValid バリデーション実行フラグ
	 * @param isEvent イベント情報使用フラグ
	 */
	protected addListAction(name: string, listName: string, actionMethod: (param: any, item: any) => void, isValid: boolean = false, isEvent: boolean = false): void {
		this.m_ListInfoMap.addListAction(name, listName, actionMethod, isValid, isEvent);
	}

	/**
	 * リストロストフォーカスアクション登録処理
	 * @param name アクション名
	 * @param listName リスト名
	 * @param actionMethod リストアクションメソッド
	 * @param isValid バリデーション実行フラグ
	 * @param isEvent イベント情報使用フラグ
	 */
	protected addListBlurAction(name: string, listName: string, actionMethod: (param: any) => void, isValid: boolean = false, isEvent: boolean = false): void {
		const aname = `${name}@@blur`;
		const ainfo = this.m_ListInfoMap.addListAction(aname, listName, actionMethod, isValid, isEvent);
		if (ainfo != null)
			ainfo.isBlur = true;
	}

	/**
	 * リストコントロール値取得
	 * @param name コントロール名
	 * @param listName リスト名
	 * @param row 行情報
	 * @returns リストコントロール値
	 */
	protected getListControlValue(name: string, listName: string, row: any): any {
		return this.m_ListInfoMap.getControlValue(name, listName, row);
	}

	/**
	 * リストコントロール情報取得
	 * @param name コントロール名
	 * @param listName リスト名
	 * @param row 行情報
	 * @returns リストコントロール情報
	 */
	protected getListControl(name: string, listName: string, row: any): WprControlInfo {
		return this.m_ListInfoMap.getControl(name, listName, row);
	}

	/**
	 * リスト情報取得処理
	 * @param listName リスト名
	 * @returns リスト情報
	 */
	public getListInfo(listName: string): WprListInfo {
		return this.m_ListInfoMap.getListInfo(listName);
	}

	/**
	 * リストデータ設定処理
	 * @param tableName リスト名
	 * @param tableData リストデータ
	 */
	public abstract setListData(listName: string, listData: Array<any>): void;

	/**
	 * リストデータをビューに反映する
	 * @param listName リスト名
	 */
	public abstract refreshListView(listName?: string): void;

	/**
	 * リストデータをビューに反映する
	 * @param listName リスト名
	 */
	public abstract refreshListModel(listName?: string): void;

	/**
	 * リスト情報クリア処理
	 */
	public abstract clearListInfo(): void;
	// --------------------------------------------------------------------------

	// private メソッド  ---------------------------------------------------------
	/**
	 * モデルマップ取得
	 * @returns モデルマップ
	 */
	private getModelMap(): Map<string, string> {
		if (!this.m_ModelMap)
			this.m_ModelMap = this.FW.model.getDecoNameMap('ModelName', this);
		return this.m_ModelMap;
	}

	/**
	 * エラーリスト再表示処理
	 */
	private refreshViewErrorList(): void {
		let msgList = new Array();
		if (this.m_InputErrorMsgList != null) {
			for(const info of this.m_InputErrorMsgList)
				msgList.push(info);
		}
		this.controlMap.forEach((cinfo, key, map) => {
			if (cinfo.validError != null) {
				for(const info of cinfo.validError.errorInfoList) {
					if (msgList.includes(info) === false)
						msgList.push(info);
				}
			}
		});
		this.listInfoMap.getValidErrorList().forEach(validError => {
			for(const info of validError.errorInfoList) {
				if (msgList.includes(info) === false)
					msgList.push(info);
			}
		});
		if (msgList.length === 0)
			msgList = null;
		this.m_ViewErrorList.forEach(viewError => {
			viewError.setErrorList(msgList);
		});
	}
	// --------------------------------------------------------------------------
}
